import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiModeDealService } from '@vanilla/services/ApiModeDealService.js';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { CacheService } from '@vanilla/services/CacheService.js';
import { UserService } from '@vanilla/services/UserService.js';
import { ApiModeDataService } from '@vanilla/services/crm/ApiModeDataService.js';
import { legacyFilterCompat } from '@vanilla/utils/CrmDealFilters.js';
import { RevenueSetupService } from '@vanilla/services/RevenueSetupService.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { RealtimeService } from '@vanilla/services/RealtimeService.js';
import { buildFilterState, getPeriodLabel } from '../lib/period.js';
import { resolveDealOwnerScope } from './dealOwnerScope.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { SyncManager } from '@vanilla/reference/SyncManager.js';
import { createDebouncedRefresh } from '@vanilla/utils/debouncedRefresh.js';
import {
    applyDealRealtimePatch,
    dealRowFromApiPayload,
    mergeDealRowsById,
} from './applyDealRealtimePatch.js';
import { LIST_VIEW_MAX_PAGES, LIST_VIEW_PER_PAGE } from './listViewPaging.js';

export const DEFAULT_FILTERS = {
    scope: 'all',
    dimension: 'created',
    createdPeriodType: 'year',
    createdPeriodOffset: 0,
    createdStart: '',
    createdEnd: '',
    revenuePeriodType: 'year',
    revenuePeriodOffset: 0,
    revenueStart: '',
    revenueEnd: '',
    ownerIds: [],
    accountIds: [],
    dateRangeAll: true,
    search: ''
};

const FILTER_QUERY_KEYS = new Set([
    'search', 'scope', 'owner', 'period', 'dimension', 'accounts',
    'createdStart', 'createdEnd', 'revenueStart', 'revenueEnd',
    'createdOffset', 'revenueOffset', 'dateRangeAll',
]);

function filtersFromSearchParams(params) {
    const next = { ...DEFAULT_FILTERS };
    const search = params.get('search');
    if (search != null) next.search = search;
    const scope = params.get('scope');
    if (scope === 'my' || scope === 'all') next.scope = scope;
    const owner = params.get('owner');
    if (owner) next.ownerIds = owner.split(',').map((s) => s.trim()).filter(Boolean);
    const period = params.get('period');
    if (period) next.createdPeriodType = period;
    const dimension = params.get('dimension');
    if (dimension === 'created' || dimension === 'revenue') next.dimension = dimension;
    const accounts = params.get('accounts');
    if (accounts) next.accountIds = accounts.split(',').map((s) => s.trim()).filter(Boolean);
    if (params.get('createdStart')) next.createdStart = params.get('createdStart');
    if (params.get('createdEnd')) next.createdEnd = params.get('createdEnd');
    if (params.get('revenueStart')) next.revenueStart = params.get('revenueStart');
    if (params.get('revenueEnd')) next.revenueEnd = params.get('revenueEnd');
    const createdOffset = params.get('createdOffset');
    if (createdOffset != null && createdOffset !== '') next.createdPeriodOffset = Number(createdOffset) || 0;
    const revenueOffset = params.get('revenueOffset');
    if (revenueOffset != null && revenueOffset !== '') next.revenuePeriodOffset = Number(revenueOffset) || 0;
    const dateRangeAll = params.get('dateRangeAll');
    if (dateRangeAll === '0' || dateRangeAll === 'false') next.dateRangeAll = false;
    if (dateRangeAll === '1' || dateRangeAll === 'true') next.dateRangeAll = true;
    return next;
}

function applyFiltersToSearchParams(params, filters) {
    const next = new URLSearchParams(params);
    FILTER_QUERY_KEYS.forEach((k) => next.delete(k));
    if (filters.search) next.set('search', filters.search);
    if (filters.scope && filters.scope !== 'all') next.set('scope', filters.scope);
    if (filters.ownerIds?.length) next.set('owner', filters.ownerIds.join(','));
    if (filters.createdPeriodType && filters.createdPeriodType !== 'year') next.set('period', filters.createdPeriodType);
    if (filters.dimension && filters.dimension !== 'created') next.set('dimension', filters.dimension);
    if (filters.accountIds?.length) next.set('accounts', filters.accountIds.join(','));
    if (filters.createdStart) next.set('createdStart', filters.createdStart);
    if (filters.createdEnd) next.set('createdEnd', filters.createdEnd);
    if (filters.revenueStart) next.set('revenueStart', filters.revenueStart);
    if (filters.revenueEnd) next.set('revenueEnd', filters.revenueEnd);
    if (filters.createdPeriodOffset) next.set('createdOffset', String(filters.createdPeriodOffset));
    if (filters.revenuePeriodOffset) next.set('revenueOffset', String(filters.revenuePeriodOffset));
    if (filters.dateRangeAll === false) next.set('dateRangeAll', '0');
    return next;
}

function invalidateDealCaches() {
    try {
        CacheService.invalidate('deals:all');
        CacheService.invalidate('schedules:all');
        CrmApiRepository.clearCache();
    } catch (err) {
        console.warn('[useDeals] cache invalidation failed', err);
    }
}

export function useDeals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [deals, setDeals] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recordScope, setRecordScope] = useState('all');
    const [scopedOwnerIds, setScopedOwnerIds] = useState([]);
    const [scopedOwners, setScopedOwners] = useState([]);
    const inflight = useRef(null);
    const lastSyncedAt = useRef(null);
    const fetchIdsInflight = useRef(new Set());
    const skipUrlWrite = useRef(true);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const load = useCallback(async (opts = {}) => {
        const { force } = opts;
        if (inflight.current && !force) return inflight.current;
        setLoading(true);
        setError('');
        const promise = (async () => {
            try {
                if (force) invalidateDealCaches();
                await Promise.all([
                    SyncManager.ensureCoreReference().catch(() => {}),
                    PipelineStore.initialize().catch(() => {}),
                ]);
                const [listView, allSchedules, allClients, allUsers] = await Promise.all([
                    ApiModeDealService.getWorkspaceListView({
                        maxPages: LIST_VIEW_MAX_PAGES,
                        perPage: LIST_VIEW_PER_PAGE,
                    }),
                    CrmApiRepository.getAllSchedules().catch(() => []),
                    ApiModeDataService.getClientsAll().catch(() => []),
                    UserService.getAssignableUsers().catch(() => []),
                ]);
                setDeals(listView.deals);
                setSchedules(allSchedules);
                setClients(allClients);
                setUsers(allUsers);
                lastSyncedAt.current = new Date().toISOString();
                if (listView.truncated && typeof console !== 'undefined') {
                    console.warn(
                        `[useDeals] list-view capped at ${listView.deals.length} of ${listView.total} deals`
                    );
                }
                try {
                    await permissions.load().catch(() => {});
                    const scope = await resolveDealOwnerScope(allUsers);
                    setRecordScope(scope.recordScope);
                    setScopedOwnerIds(scope.scopedOwnerIds);
                    setScopedOwners(scope.scopedOwners);
                } catch (scopeErr) {
                    console.warn('[useDeals] owner scope resolve failed', scopeErr);
                }
            } catch (err) {
                console.error('[useDeals] failed to load', err);
                setError(err?.message || 'Failed to load deals.');
            } finally {
                setLoading(false);
            }
        })();
        inflight.current = promise;
        try {
            await promise;
        } finally {
            inflight.current = null;
        }
    }, []);

    const fetchAndInsertDeal = useCallback(async (dealId) => {
        const id = String(dealId);
        if (!id || fetchIdsInflight.current.has(id)) return;
        fetchIdsInflight.current.add(id);
        try {
            const raw = await DealsApiService.getById(id);
            const row = dealRowFromApiPayload(raw);
            if (!row) return;
            setDeals((prev) => {
                if (prev.some((d) => String(d.id) === id)) {
                    return mergeDealRowsById(prev, [row]);
                }
                return [row, ...prev];
            });
            lastSyncedAt.current = new Date().toISOString();
        } catch (err) {
            console.warn('[useDeals] single-deal fetch failed', id, err);
        } finally {
            fetchIdsInflight.current.delete(id);
        }
    }, []);

    const mergeDeltaSince = useCallback(async () => {
        const since = lastSyncedAt.current;
        if (!since) return;
        try {
            const rows = await CrmApiRepository.getDealsListViewUpdatedSince(since);
            if (rows?.length) {
                setDeals((prev) => mergeDealRowsById(prev, rows));
            }
            lastSyncedAt.current = new Date().toISOString();
        } catch (err) {
            console.warn('[useDeals] delta sync failed; soft full reload', err);
            await load({ force: true });
        }
    }, [load]);

    useEffect(() => {
        load({});
        const onMutation = () => load({ force: true });
        const onSoftRefresh = createDebouncedRefresh(() => load({ force: true }), 400);
        const onDelta = createDebouncedRefresh(() => { void mergeDeltaSince(); }, 400);

        const onActivitiesChanged = (e) => {
            const detail = e.detail || {};
            const dealId = detail.dealId;
            if (!dealId) return;
            setDeals((prev) => prev.map((d) => {
                if (String(d.id) !== String(dealId)) return d;
                const next = { ...d };
                if (detail.nextStep !== undefined) next.nextStep = detail.nextStep;
                if (Array.isArray(detail.nextSteps)) next.nextSteps = detail.nextSteps;
                if (detail.pendingNextStepCount != null) next.pendingNextStepCount = detail.pendingNextStepCount;
                if (detail.pendingTaskCount != null) next.pendingTaskCount = detail.pendingTaskCount;
                return next;
            }));
        };

        const unsub = RealtimeService.subscribe((msg) => {
            if (msg?.type === 'connected') {
                onDelta();
                return;
            }
            if (msg?.type !== 'dealChanged') return;
            setDeals((prev) => {
                const { deals: next, fetchId } = applyDealRealtimePatch(prev, msg);
                if (fetchId) void fetchAndInsertDeal(fetchId);
                return next;
            });
            if (msg.eventType === 'deal.created' || msg.eventType === 'deal.deleted') {
                // Schedules graph may change with create/delete — soft refresh schedules only.
                void CrmApiRepository.getAllSchedules()
                    .then((all) => setSchedules(all || []))
                    .catch(() => {});
            }
        });

        const onFocus = () => onDelta();

        window.addEventListener('dealSaved', onMutation);
        window.addEventListener('dealDeleted', onMutation);
        window.addEventListener('dataImported', onMutation);
        window.addEventListener('clientSaved', onSoftRefresh);
        window.addEventListener('userSaved', onSoftRefresh);
        window.addEventListener('dealActivitiesChanged', onActivitiesChanged);
        window.addEventListener('focus', onFocus);
        return () => {
            onSoftRefresh.cancel();
            onDelta.cancel();
            window.removeEventListener('dealSaved', onMutation);
            window.removeEventListener('dealDeleted', onMutation);
            window.removeEventListener('dataImported', onMutation);
            window.removeEventListener('clientSaved', onSoftRefresh);
            window.removeEventListener('userSaved', onSoftRefresh);
            window.removeEventListener('dealActivitiesChanged', onActivitiesChanged);
            window.removeEventListener('focus', onFocus);
            try { unsub?.(); } catch {}
        };
    }, [load, fetchAndInsertDeal, mergeDeltaSince]);

    const setFilter = useCallback((patch) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({ ...DEFAULT_FILTERS });
    }, []);

    useEffect(() => {
        const fromUrl = filtersFromSearchParams(searchParams);
        const serialized = JSON.stringify(fromUrl);
        if (serialized === JSON.stringify(filtersRef.current)) return undefined;
        skipUrlWrite.current = true;
        setFilters(fromUrl);
        const t = setTimeout(() => { skipUrlWrite.current = false; }, 0);
        return () => clearTimeout(t);
    }, [searchParams]);

    useEffect(() => {
        if (skipUrlWrite.current) return undefined;
        const timer = setTimeout(() => {
            setSearchParams((prev) => {
                const next = applyFiltersToSearchParams(prev, filtersRef.current);
                if (next.toString() === prev.toString()) return prev;
                return next;
            }, { replace: true });
        }, 300);
        return () => clearTimeout(timer);
    }, [filters, setSearchParams]);

    const clearFilter = useCallback((key) => {
        setFilters((prev) => {
            const next = { ...prev };
            switch (key) {
                case 'scope':
                    next.scope = 'all';
                    break;
                case 'dimension':
                    next.dimension = 'created';
                    break;
                case 'search':
                    next.search = '';
                    break;
                case 'ownerIds':
                    next.ownerIds = [];
                    break;
                case 'accountIds':
                    next.accountIds = [];
                    break;
                default:
                    break;
            }
            return next;
        });
    }, []);

    const filteredDeals = useMemo(() => {
        const built = {
            ...buildFilterState(filters),
            recordScope,
            scopedOwnerIds,
        };
        const list = legacyFilterCompat(deals, built, schedules);
        const schedulesByDeal = new Map();
        schedules.forEach((s) => {
            const arr = schedulesByDeal.get(s.dealId) || [];
            arr.push(s);
            schedulesByDeal.set(s.dealId, arr);
        });
        list.forEach((d) => {
            d.revenueCompletionPct = RevenueSetupService.computeCompletionPct(d, schedulesByDeal.get(d.id) || []);
        });
        return list;
    }, [deals, schedules, filters, recordScope, scopedOwnerIds]);

    const applyOwnerScope = useCallback((scope) => {
        if (!scope) return;
        setRecordScope(scope.recordScope);
        setScopedOwnerIds(scope.scopedOwnerIds || []);
        setScopedOwners(scope.scopedOwners || []);
    }, []);

    const periodLabel = useMemo(() => {
        return getPeriodLabel(buildFilterState(filters));
    }, [filters]);

    return {
        deals: filteredDeals,
        allDeals: deals,
        schedules,
        clients,
        users,
        scopedOwners,
        filters,
        periodLabel,
        loading,
        error,
        setFilter,
        resetFilters,
        clearFilter,
        applyOwnerScope,
        refresh: useCallback(() => load({ force: true }), [load])
    };
}
